/**
 * The height of tide RIGHT NOW, or an honest null.
 *
 * One function, because the alternative is the condition written out at every
 * site that wants a tide, and that is how the dangerous version of this bug
 * happened in the first place. Three separate things all have to hold before a
 * number here means anything, and each of them was being missed somewhere:
 *
 * 1. **The tide model has to cover this water.** `getHeightPort(id)` answers any
 *    unrecognised id with `HEIGHT_PORTS[0]`, which is Victoria, a fictional
 *    Namley port. It takes no position, so geography never enters into it. The
 *    training model's events all sit in a 2019 window the training scrubber
 *    never leaves, so `heightAt` returns a real, plausible-looking number rather
 *    than null, and that number was reaching the depth-under-keel alarm on real
 *    NOAA charts. A fictional tide added to a real charted depth is the worst
 *    kind of wrong: confident, plausible, and about how much water is under the
 *    boat.
 *
 * 2. **The selected source has to be the one we can actually read.** Choosing
 *    Admiralty or FES used to change nothing at these sites: they read the
 *    training port regardless, so the setting was decorative where it mattered
 *    most. Until those sources are wired through to the alarm they answer null
 *    here, which reads as chart datum, which is the cautious way to be wrong.
 *
 * 3. **The port has to genuinely exist.** Looked up strictly, with no
 *    silent fall back to the first one in the list.
 *
 * Null means "we do not know", and every consumer already treats that as chart
 * datum. That is the reading that makes us too careful rather than not careful
 * enough, which is the only acceptable direction for this number.
 */
import { trainingTideCovers } from '@/chart-sources/providers';
import { findHeightPort, heightAt } from './heights';
import type { TideHeightSourceId } from './tideSources';

export interface TideNowInput {
  /** The active chart provider id, which decides whether the model covers here. */
  providerId: string;
  /** The user's selected height source. */
  heightSourceId: TideHeightSourceId;
  /** The active reference port id. */
  portId: string;
  /** The time to read, usually the (possibly scrubbed) stream time. */
  ms: number;
}

/**
 * Whether a tide reading is available at all for this setup, without computing
 * one. Useful for a UI that wants to say "no tidal data for this area" rather
 * than show an empty field.
 */
export function tideDataCovers(providerId: string, heightSourceId: TideHeightSourceId): boolean {
  return heightSourceId === 'training' && trainingTideCovers(providerId);
}

/** The height of tide now in metres, or null when we genuinely do not know. */
export function heightNowM(input: TideNowInput): number | null {
  if (!tideDataCovers(input.providerId, input.heightSourceId)) return null;
  const port = findHeightPort(input.portId);
  if (!port) return null;
  return heightAt(port, input.ms);
}

/**
 * The reference port to NAME in the UI, or null. Same gate as the reading, so a
 * panel can never name a port it is not actually reading from. Naming a
 * fictional Namley port over real water invites a skipper to believe we have
 * tidal data for where they are pressing.
 */
export function tidePortNow(
  providerId: string,
  heightSourceId: TideHeightSourceId,
  portId: string,
): ReturnType<typeof findHeightPort> {
  if (!tideDataCovers(providerId, heightSourceId)) return undefined;
  return findHeightPort(portId);
}
